import axios from "axios";
import configs from "Config";
import { ehRotaDeAutenticacaoSemRedirecionamento } from "./rotasAutenticacao";

// Config.ts já valida as configurações ao ser importado
export const baseURL = configs.REACT_APP_BASE_URL;
export const baseURLAntigo = configs.REACT_APP_BASE_URL_ANTIGO;
export const dashboardAPIURL = configs.REACT_APP_DASHBOARD_API;

/** Endereço da tela de login do MOF novo, respeitando o REACT_APP_BASENAME do ambiente. */
export function urlLogin(): string {
  const base = (configs.REACT_APP_BASENAME ?? "").replace(/\/+$/, "");
  return `${base}/login`;
}

/** Encerra a sessão local (o token fica no sessionStorage) e volta para o login do MOF novo. */
export function irParaLogin(): void {
  sessionStorage.clear();
  window.location.replace(urlLogin());
}

function ehChamadaDeAutenticacao(error: any): boolean {
  return ehRotaDeAutenticacaoSemRedirecionamento(error?.config?.url);
}

export const api = axios.create({
  baseURL: baseURL,
  headers: { "Content-Type": "application/json" },
});

export const apiAntigo = axios.create({
  baseURL: baseURLAntigo,
  headers: { "Content-Type": "application/json" },
});

apiAntigo.interceptors.request.use((config) => {
  const token = sessionStorage.getItem("@MOF:token");

  if (token != null && !config.headers.getAuthorization()) {
    config.headers.setAuthorization(`Bearer ${token}`);
  }
  return config;
});

apiAntigo.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    // Erro de conexão (API antiga não está rodando)
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error' || !error.response) {
      const errorMsg = 
        `❌ Não foi possível conectar à API Antiga em: ${baseURLAntigo}\n\n` +
        `Possíveis causas:\n` +
        `1. A API antiga não está rodando (execute: .\scripts\start-backend-antigo.ps1)\n` +
        `2. URL incorreta em public/index.html (window.configs.REACT_APP_BASE_URL_ANTIGO)\n` +
        `3. Firewall bloqueando a conexão\n\n` +
        `URL configurada: ${baseURLAntigo}`;
      
      console.error(errorMsg);
      
      error.userMessage = 
        `Não foi possível conectar à API antiga.\n` +
        `Verifique se o backend está rodando em ${baseURLAntigo}`;
    }
    
    // Não redirecionar automaticamente em 401
    // Permitir que o código que fez a chamada trate o erro
    return Promise.reject(error.response || error);
  }
);

api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem("@MOF:token");

  if (token != null && !config.headers.getAuthorization()) {
    config.headers.setAuthorization(`Bearer ${token}`);
  }
  return config;
});

api.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    // Erro 401: sessão vencida/inválida → tela de login do MOF novo (plano de 07/10/2026, B1.7).
    // As chamadas Autenticacao/* ficam de fora: a tela de login trata o próprio 401.
    if (error.response?.status === 401 && !ehChamadaDeAutenticacao(error)) {
      irParaLogin();
      return Promise.reject(error.response);
    }
    
    // Erro de conexão (API não está rodando)
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error' || !error.response) {
      const errorMsg = 
        `❌ Não foi possível conectar à API Nova em: ${baseURL}\n\n` +
        `Possíveis causas:\n` +
        `1. A API não está rodando (execute: .\scripts\start-backend-novo.ps1)\n` +
        `2. URL incorreta em public/index.html (window.configs.REACT_APP_BASE_URL)\n` +
        `3. Firewall bloqueando a conexão\n\n` +
        `URL configurada: ${baseURL}`;
      
      console.error(errorMsg);
      
      error.userMessage = 
        `Não foi possível conectar à API.\n` +
        `Verifique se o backend está rodando em ${baseURL}`;
    }
    
    // Erro 404: Endpoint não encontrado
    if (error?.response?.status === 404) {
      console.error(
        `❌ Endpoint não encontrado: ${error.config?.method?.toUpperCase()} ${error.config?.url}\n` +
        `URL completa: ${error.config?.baseURL}${error.config?.url}\n` +
        `Verifique se a rota da API está correta.`
      );
    }
    
    return Promise.reject(error.response);
  }
);

export const apiDashboard = axios.create({
  baseURL: dashboardAPIURL,
  headers: { "Content-Type": "application/json" },
});

apiDashboard.interceptors.request.use((config) => {
  const token = sessionStorage.getItem("@MOF:token");

  if (token != null && !config.headers.getAuthorization()) {
    config.headers.setAuthorization(`Bearer ${token}`);
  }
  return config;
});

apiDashboard.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    if (error.response?.status === 401 && !ehChamadaDeAutenticacao(error)) {
      irParaLogin();
    }
    return Promise.reject(error.response);
  }
);

export const setAuthorization = (token: string | null): void => {
  api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  apiAntigo.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  apiDashboard.defaults.headers.common["Authorization"] = `Bearer ${token}`;
};

/**
 * Remove o token das três instâncias, nos dois lugares onde ele é gravado: headers.common (ponte
 * ?hashId= — tokenHelper) e headers (login/troca — hooks/auth). Usado no Sair (revisão de 08/10/2026).
 */
export const limparAuthorization = (): void => {
  for (const instancia of [api, apiAntigo, apiDashboard]) {
    delete instancia.defaults.headers.common["Authorization"];
    delete instancia.defaults.headers.Authorization;
  }
};
